import "./userProfileModern.css";
import Header from "../../Components/Shared/Header/Header";
import Footer from "../../Components/Shared/Footer/Footer";
import PhotoManager from "../../Components/PhotoManager/PhotoManager";
import { useEffect, useState } from "react";
import VivahApi from "../../api";
import axios from "axios";
import { calculateAgeInYearsAndMonths } from "../../utils/common";
import { getMasterDataItem } from "../../utils/api-utils";

function UserProfileModern(props) {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        // Fetch user profile
        const res = await VivahApi.Auth.getUserProfileData();
        const profileData = res.data;
        
        // Fetch master data in parallel
        const [religionsRes, castesRes, languagesRes, statesRes, citiesRes, occupationsRes, incomesRes, maritalStatusRes, dietsRes, bloodGroupsRes, bodyTypesRes, familyStatusRes, heightsRes, qualificationsRes, professionTypesRes] = await Promise.all([
          VivahApi.Religion.listReligionWithCaste(),
          VivahApi.Religion.listGothras(),
          VivahApi.Religion.listMotherTongues(),
          VivahApi.Religion.listStates(),
          VivahApi.Religion.listCities(),
          axios.get('/master-data/occupations.json'),
          axios.get('/master-data/incomes.json'),
          axios.get('/master-data/marital-status.json'),
          axios.get('/master-data/diets.json'),
          axios.get('/master-data/blood-groups.json'),
          axios.get('/master-data/body-types.json'),
          axios.get('/master-data/family-status.json'),
          axios.get('/master-data/heights.json'),
          axios.get('/master-data/qualifications.json'),
          axios.get('/master-data/profession-types.json')
        ]);
        
        const religions = religionsRes.data;
        const castes = castesRes.data;
        const languages = languagesRes.data;
        const states = statesRes.data;
        const cities = citiesRes.data;
        const occupations = occupationsRes.data;
        const incomes = incomesRes.data;
        const maritalStatuses = maritalStatusRes.data;
        const diets = dietsRes.data;
        const bloodGroups = bloodGroupsRes.data;
        const bodyTypes = bodyTypesRes.data;
        const familyStatuses = familyStatusRes.data;
        const heights = heightsRes.data;
        const qualifications = qualificationsRes.data;
        const professionTypes = professionTypesRes.data;
        
        // Family Values master data
        const familyValues = [
          {id: 1, label: "Traditional", name: "Traditional"},
          {id: 2, label: "Moderate", name: "Moderate"},
          {id: 3, label: "Liberal", name: "Liberal"}
        ];
        
        // Health Information master data
        const healthInfo = [
          {id: 1, label: "No Issues", name: "No Issues"},
          {id: 2, label: "Minor Health Issues", name: "Minor Health Issues"},
          {id: 3, label: "Major Health Issues", name: "Major Health Issues"},
          {id: 4, label: "Prefer not to say", name: "Prefer not to say"}
        ];
        
        // Disabilities master data
        const disabilities = [
          {id: 1, label: "None", name: "None"},
          {id: 2, label: "Physical Disability", name: "Physical Disability"},
          {id: 3, label: "Visual Impairment", name: "Visual Impairment"},
          {id: 4, label: "Hearing Impairment", name: "Hearing Impairment"},
          {id: 5, label: "Other", name: "Other"}
        ];
        
        // Map IDs to names from master data
        const userData = profileData.user || {};
        
        const enrichedProfile = {
          ...userData,
          user: userData,
          religion: getMasterDataItem(religions, userData.religionId),
          caste: getMasterDataItem(castes, userData.casteId),
          motherTongue: getMasterDataItem(languages, userData.motherTongueId),
          state: getMasterDataItem(states, userData.stateId),
          city: getMasterDataItem(cities, userData.cityId),
          familyState: getMasterDataItem(states, userData.familyStateId),
          familyCity: getMasterDataItem(cities, userData.familyCityId),
          height: getMasterDataItem(heights, userData.heightId),
          profession: getMasterDataItem(occupations, userData.professionId),
          professionType: getMasterDataItem(professionTypes, userData.professionTypeId),
          qualification: getMasterDataItem(qualifications, userData.qualificationId),
          income: getMasterDataItem(incomes, userData.incomeId),
          maritalStatus: getMasterDataItem(maritalStatuses, userData.maritalStatusId),
          diet: getMasterDataItem(diets, userData.dietId),
          bloodGroup: getMasterDataItem(bloodGroups, userData.bloodGroupId),
          bodyType: getMasterDataItem(bodyTypes, userData.bodyTypeId),
          familyStatus: getMasterDataItem(familyStatuses, userData.familyStatusId),
          familyValues: getMasterDataItem(familyValues, userData.familyValuesId),
          healthInformation: getMasterDataItem(healthInfo, userData.healthInformationId),
          disability: getMasterDataItem(disabilities, userData.disabilityId),
          fatherOccupation: getMasterDataItem(occupations, userData.fatherOccupationId),
          motherOccupation: getMasterDataItem(occupations, userData.motherOccupationId)
        };
        
        setProfile(enrichedProfile);
      } catch (error) {
        console.error("Error loading profile:", error);
        setProfile({});
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Initialize jQuery features after component mounts
  useEffect(() => {
    // Apply background color from data-color attribute
    const pageTitle = document.querySelector('.gla_page_title[data-color]');
    if (pageTitle) {
      const color = pageTitle.getAttribute('data-color');
      if (color) {
        pageTitle.style.backgroundColor = color;
      }
    }
  }, [loading]); // Run after loading completes

  // Calculate profile completeness
  const calculateCompleteness = () => {
    const fields = [
      'firstName', 'lastName', 'dateOfBirth', 'heightId', 'religionId', 'casteId',
      'motherTongueId', 'stateId', 'cityId', 'qualificationId', 'professionId',
      'incomeId', 'maritalStatusId', 'dietId', 'bloodGroupId', 'bodyTypeId',
      'aboutYourself', 'phone', 'fatherName', 'motherName'
    ];
    
    const filledFields = fields.filter(field => profile.user?.[field] || profile[field]);
    const percentage = Math.round((filledFields.length / fields.length) * 100);
    const missingFields = fields.filter(field => !(profile.user?.[field] || profile[field]));
    
    return { percentage, missingFields: missingFields.slice(0, 5) };
  };

  const renderValue = (value, valueName = null) => {
    if (!value && !valueName) {
      return <span className="info-value not-specified">Not Specified</span>;
    }
    return <span className="info-value">{valueName || value}</span>;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="modern-profile-container">
          <div className="container">
            <div className="profile-loading">
              <div className="loading-spinner"></div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const { percentage, missingFields } = calculateCompleteness();
  const age = profile.user?.dateOfBirth ? calculateAgeInYearsAndMonths(new Date(profile.user.dateOfBirth)) : 'N/A';
  const fullName = `${profile.user?.firstName || ''} ${profile.user?.lastName || ''}`.trim() || 'User';

  return (
    <>
      <div className="gla_page" id="gla_page">
        <Header />
        
        <div
          className="gla_page_title gla_image_bck gla_wht_txt"
          data-color="#282828"
        >
          <div className="container text-left">
            <div className="row">
              <div className="col-md-8">
                <h1 className="gla_h1_title">My Profile</h1>
              </div>
              <div className="col-md-4">
                <div className="breadcrumbs">
                  <a href="/">Home</a>
                  <span>My Profile</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="modern-profile-container">
          <div className="container">
            {/* Profile Header */}
            <div className="profile-header">
              <div className="profile-header-content">
                <div className="profile-image-wrapper">
                  <div className="profile-image-container">
                    <img
                      src={(profile.photos && profile.photos[0]?.url) || profile.photo || "assets/images/avatar-placeholder.svg"}
                      alt={fullName}
                    />
                  </div>
                  <div className="verification-badge" title="Email Verified">
                    <i className="ti ti-check"></i>
                  </div>
                </div>
                
                <div className="profile-header-info">
                  <h1 className="profile-name">{fullName}</h1>
                  <p className="profile-tagline">
                    {age} • {profile.city?.name || 'Not Specified'} • {profile.profession?.name || 'Professional'}
                  </p>
                  
                  <div className="profile-quick-stats">
                    <div className="quick-stat-item">
                      <i className="ti ti-calendar"></i>
                      <span>{age}</span>
                    </div>
                    <div className="quick-stat-item">
                      <i className="ti ti-direction-alt"></i>
                      <span>{profile.height?.name || 'Height N/A'}</span>
                    </div>
                    <div className="quick-stat-item">
                      <i className="ti ti-shine"></i>
                      <span>{profile.religion?.name || 'Religion N/A'}</span>
                    </div>
                    <div className="quick-stat-item">
                      <i className="ti ti-location-pin"></i>
                      <span>{profile.city?.name || 'Location N/A'}</span>
                    </div>
                  </div>
                  
                  <div className="profile-actions">
                    <a href="/update-profile" className="btn-edit-profile">
                      <i className="ti ti-pencil"></i> Edit Profile
                    </a>
                    <button className="btn-share-profile">
                      <i className="ti ti-share"></i> Share Profile
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Completeness */}
            <div className="profile-completeness">
              <div className="completeness-header">
                <span className="completeness-title">Profile Completeness</span>
                <span className="completeness-percentage">{percentage}%</span>
              </div>
              <div className="progress-bar-container">
                <div className="progress-bar-fill" style={{width: `${percentage}%`}}></div>
              </div>
              {missingFields.length > 0 && (
                <div className="missing-fields">
                  <span style={{fontSize: '0.9rem', color: '#666', marginRight: '0.5rem'}}>Missing:</span>
                  {missingFields.map((field, idx) => (
                    <span key={idx} className="missing-field-tag">
                      {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Membership Section */}
            {(() => {
              const localPlanName = localStorage.getItem('planName');
              const isPremium = profile.isPremium === 'yes' || !!localPlanName;
              const planLabel = profile.premiumPlanId ? (localPlanName || 'Premium') : (localPlanName || null);
              const expiry = profile.premiumPlanExpiry;
              return (
                <div className="profile-completeness" style={{marginBottom: '1rem'}}>
                  <div className="completeness-header">
                    <span className="completeness-title"><i className="ti ti-crown" style={{marginRight: '6px', color: '#e91e63'}}></i>Membership</span>
                    {isPremium
                      ? <span style={{background:'linear-gradient(135deg,#e91e63,#ff6090)',color:'#fff',padding:'2px 12px',borderRadius:'12px',fontSize:'0.82rem',fontWeight:600}}>{planLabel || 'Premium'}</span>
                      : <span style={{background:'#eee',color:'#888',padding:'2px 12px',borderRadius:'12px',fontSize:'0.82rem'}}>Free</span>
                    }
                  </div>
                  {isPremium ? (
                    <p style={{margin:'8px 0 0',fontSize:'0.9rem',color:'#555'}}>
                      {expiry
                        ? <>Plan active until <strong>{expiry}</strong></>
                        : <>Active plan: <strong>{planLabel || 'Premium'}</strong></>}
                    </p>
                  ) : (
                    <p style={{margin:'8px 0 0',fontSize:'0.9rem',color:'#888'}}>
                      <a href="/update" style={{color:'#e91e63',fontWeight:600}}>Upgrade to Premium</a> to unlock all features.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* About Section */}
            {profile.aboutYourself && (
              <div className="about-section">
                <h3><i className="ti ti-comment"></i> About Me</h3>
                <p className="about-text">{profile.aboutYourself}</p>
              </div>
            )}

            {/* Stats Cards */}
            <div className="stats-cards">
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="ti ti-briefcase"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Profession</div>
                  <div className="stat-value">{profile.profession?.name || 'N/A'}</div>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="ti ti-money"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Annual Income</div>
                  <div className="stat-value">{profile.income?.name || 'N/A'}</div>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="ti ti-shield"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Education</div>
                  <div className="stat-value">{profile.qualification?.name || 'N/A'}</div>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="ti ti-heart"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-label">Marital Status</div>
                  <div className="stat-value">{profile.maritalStatus?.name || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Info Cards Grid */}
            <div className="info-cards-grid">
              {/* Basics & Lifestyle */}
              <div className="info-card basics">
                <div className="card-header">
                  <div className="card-icon">
                    <i className="ti ti-user"></i>
                  </div>
                  <h3 className="card-title">Basics & Lifestyle</h3>
                </div>
                <div className="info-items">
                  <div className="info-item">
                    <span className="info-label">Age</span>
                    {renderValue(age)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Date of Birth</span>
                    {renderValue(profile.user?.dateOfBirth ? new Date(profile.user.dateOfBirth).toLocaleDateString() : null)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Height</span>
                    {renderValue(profile.height?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Weight</span>
                    {renderValue(profile.bodyWeight ? `${profile.bodyWeight} kg` : null)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Body Type</span>
                    {renderValue(profile.bodyType?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Complexion</span>
                    {renderValue(profile.complexion)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Blood Group</span>
                    {renderValue(profile.bloodGroup?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Diet</span>
                    {renderValue(profile.diet?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Health Information</span>
                    {renderValue(profile.healthInformation?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Disability</span>
                    {renderValue(profile.disability?.name)}
                  </div>
                </div>
              </div>

              {/* Religious Background */}
              <div className="info-card religious">
                <div className="card-header">
                  <div className="card-icon">
                    <i className="ti ti-shine"></i>
                  </div>
                  <h3 className="card-title">Religious Background</h3>
                </div>
                <div className="info-items">
                  <div className="info-item">
                    <span className="info-label">Religion</span>
                    {renderValue(profile.religion?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Caste</span>
                    {renderValue(profile.caste?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Gothra</span>
                    {renderValue(profile.gothra)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Mother Tongue</span>
                    {renderValue(profile.motherTongue?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Birth Time</span>
                    {renderValue(profile.birthTime)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Birth Place</span>
                    {renderValue(profile.birthPlace)}
                  </div>
                </div>
              </div>

              {/* Family Details */}
              <div className="info-card family">
                <div className="card-header">
                  <div className="card-icon">
                    <i className="ti ti-home"></i>
                  </div>
                  <h3 className="card-title">Family Details</h3>
                </div>
                <div className="info-items">
                  <div className="info-item">
                    <span className="info-label">Father's Name</span>
                    {renderValue(profile.fatherName)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Father's Occupation</span>
                    {renderValue(profile.fatherOccupation?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Mother's Name</span>
                    {renderValue(profile.motherName)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Mother's Occupation</span>
                    {renderValue(profile.motherOccupation?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Brothers</span>
                    {renderValue(profile.brother)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Sisters</span>
                    {renderValue(profile.sister)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Family Values</span>
                    {renderValue(profile.familyValues?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Family Status</span>
                    {renderValue(profile.familyStatus?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Family Location</span>
                    {renderValue(profile.familyCity?.name)}
                  </div>
                </div>
              </div>

              {/* Education & Career */}
              <div className="info-card education">
                <div className="card-header">
                  <div className="card-icon">
                    <i className="ti ti-medall"></i>
                  </div>
                  <h3 className="card-title">Education & Career</h3>
                </div>
                <div className="info-items">
                  <div className="info-item">
                    <span className="info-label">Highest Qualification</span>
                    {renderValue(profile.qualification?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">College(s) Attended</span>
                    {renderValue(profile.collegeName)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Working With</span>
                    {renderValue(profile.professionType?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Working As</span>
                    {renderValue(profile.profession?.name)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Company Name</span>
                    {renderValue(profile.companyName)}
                  </div>
                  <div className="info-item">
                    <span className="info-label">Annual Income</span>
                    {renderValue(profile.income?.name)}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="contact-card">
              <h3><i className="ti ti-mobile"></i> Contact Information</h3>
              <div className="contact-items">
                <div className="contact-item">
                  <i className="ti ti-mobile"></i>
                  <div className="contact-item-content">
                    <div className="contact-item-label">Phone Number</div>
                    <div className="contact-item-value">{profile.user?.phone || 'Not Specified'}</div>
                  </div>
                </div>
                <div className="contact-item">
                  <i className="ti ti-email"></i>
                  <div className="contact-item-content">
                    <div className="contact-item-label">Email Address</div>
                    <div className="contact-item-value">{profile.user?.email || 'Not Specified'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Photo Manager */}
            <div className="photo-management-section">
              <PhotoManager 
                photos={profile.photos || []} 
                onPhotosChange={(updatedPhotos) => {
                  setProfile({ ...profile, photos: updatedPhotos });
                }}
              />
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}

export default UserProfileModern;
